// Announcements + maintenance windows management (scheduler-service). P1-6c.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, DatePicker, Empty, Form, Input, Modal, Select, Space, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { PageHeader, StatusTag } from '../../components/console';
import * as api from '../../services/announcementService';

const { Text } = Typography;

const LEVEL_COLOR: Record<string, string> = { info: 'processing', warning: 'warning', critical: 'error' };
const fmt = (iso?: string | null) => (iso ? fmtDateTime(iso) : '—');

const AnnouncementsTab: React.FC = () => {
  const [rows, setRows] = useState<api.Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.listAnnouncements()); } catch { message.error('Could not load announcements.'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    const v = await form.validateFields();
    setBusy(true);
    try {
      await api.createAnnouncement({ level: v.level, message: v.message, audience: v.audience, dismissible: v.dismissible });
      message.success('Announcement published');
      setOpen(false); form.resetFields(); await load();
    } catch { message.error('Could not publish.'); } finally { setBusy(false); }
  };

  const toggle = async (a: api.Announcement) => {
    try { await api.patchAnnouncement(a.id, { active: !a.active }); await load(); } catch { message.error('Update failed.'); }
  };

  return (
    <Card
      extra={<Space><Button size="small" icon={<ReloadOutlined />} onClick={() => void load()} /><Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>New announcement</Button></Space>}
    >
      <Table
        size="small" rowKey="id" loading={loading} dataSource={rows} pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No announcements." /> }}
        columns={[
          { title: 'Level', dataIndex: 'level', render: (l: string) => <Tag color={LEVEL_COLOR[l] || 'default'}>{l}</Tag> },
          { title: 'Message', dataIndex: 'message', ellipsis: true },
          { title: 'Audience', dataIndex: 'audience' },
          { title: 'Active', dataIndex: 'active', render: (v: boolean, a: api.Announcement) => <Switch size="small" checked={v} onChange={() => void toggle(a)} /> },
          { title: 'Starts', dataIndex: 'starts_at', render: fmt },
          { title: 'Ends', dataIndex: 'ends_at', render: fmt },
        ]}
      />
      <Modal title="Publish announcement" open={open} onOk={create} confirmLoading={busy} onCancel={() => setOpen(false)} okText="Publish">
        <Form form={form} layout="vertical" initialValues={{ level: 'info', audience: 'all', dismissible: true }}>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="level" label="Level"><Select options={['info', 'warning', 'critical'].map((v) => ({ value: v, label: v }))} /></Form.Item>
          <Form.Item name="audience" label="Audience"><Select options={['all', 'tenant_admins', 'operators'].map((v) => ({ value: v, label: v }))} /></Form.Item>
          <Form.Item name="dismissible" label="Dismissible" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

const MaintenanceTab: React.FC = () => {
  const [rows, setRows] = useState<api.MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.listMaintenance()); } catch { message.error('Could not load maintenance.'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const schedule = async () => {
    const v = await form.validateFields();
    setBusy(true);
    try {
      await api.createMaintenance({
        title: v.title, description: v.description, affected_services: v.affected_services,
        scheduled_start: v.window[0].toISOString(), scheduled_end: v.window[1].toISOString(), is_public: v.is_public,
      });
      message.success('Maintenance scheduled');
      setOpen(false); form.resetFields(); await load();
    } catch { message.error('Could not schedule.'); } finally { setBusy(false); }
  };

  const setStatus = async (m: api.MaintenanceWindow, status: string) => {
    try { await api.patchMaintenance(m.id, { status }); await load(); } catch { message.error('Update failed.'); }
  };

  return (
    <Card
      extra={<Space><Button size="small" icon={<ReloadOutlined />} onClick={() => void load()} /><Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>Schedule</Button></Space>}
    >
      <Table
        size="small" rowKey="id" loading={loading} dataSource={rows} pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No maintenance windows." /> }}
        columns={[
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          { title: 'Status', dataIndex: 'status', render: (s: string) => <StatusTag status={s} /> },
          { title: 'Public', dataIndex: 'is_public', render: (p: boolean) => (p ? <Tag color="processing">public</Tag> : <Text type="secondary">internal</Text>) },
          { title: 'Start', dataIndex: 'scheduled_start', render: fmt },
          { title: 'End', dataIndex: 'scheduled_end', render: fmt },
          {
            title: '', key: 'act', render: (_: unknown, m: api.MaintenanceWindow) => (
              <Select
                size="small" style={{ width: 130 }} value={m.status}
                onChange={(v) => void setStatus(m, v)}
                options={['scheduled', 'in_progress', 'completed', 'cancelled'].map((v) => ({ value: v, label: v }))}
              />
            ),
          },
        ]}
      />
      <Modal title="Schedule maintenance" open={open} onOk={schedule} confirmLoading={busy} onCancel={() => setOpen(false)} okText="Schedule">
        <Form form={form} layout="vertical" initialValues={{ is_public: true }}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="window" label="Window" rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="affected_services" label="Affected services">
            <Select mode="tags" tokenSeparators={[',']} placeholder="e.g. payroll, auth" />
          </Form.Item>
          <Form.Item name="is_public" label="Public (status page)" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export const AnnouncementsPage: React.FC = () => (
  <div className="p-1">
    <PageHeader title="Announcements & Maintenance" subtitle="Platform banners and scheduled maintenance windows." />
    <Tabs
      items={[
        { key: 'announcements', label: 'Announcements', children: <AnnouncementsTab /> },
        { key: 'maintenance', label: 'Maintenance', children: <MaintenanceTab /> },
      ]}
    />
  </div>
);

export default AnnouncementsPage;
